import React, { forwardRef, useCallback, useMemo } from "react"
import {
  Autocomplete as MUIAutocomplete,
  FormControl,
  FormHelperText,
  Input,
  Box,
  Typography,
  Stack,
  InputLabel,
  AutocompleteRenderOptionState,
  AutocompleteProps as MUIAutocompleteProps,
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
  AutocompleteInputChangeReason,
  InputProps
} from "@mui/material"
import { FaIcon } from "../../../../mp-core/icons"
import Chip, { ChipProps } from "../Chip"
import { StyleName, useStyles } from "./styles"
import Checkbox from "../Checkbox"
import { formatDataAutomationId } from "@masteryprep/qti-components"
import { useListboxScrollTop } from "./hooks"
import { Option } from "./types"

export type AutocompleteProps = Omit<
  MUIAutocompleteProps<Option, boolean, boolean, boolean>,
  "renderInput" | "ChipProps"
> & {
  value?: string | string[]
  onChange?: (
    event: React.SyntheticEvent,
    value: string | string[],
    reason?: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<Option>
  ) => void
  inputValue?: string
  onInputChange?: (
    event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => void
  options: Option[]
  id?: string
  name?: string
  disabled?: boolean
  label?: string
  inline?: boolean
  withCheckbox?: boolean
  withAdormentBorder?: boolean
  className?: string
  inputStartAdornment?: React.ReactNode
  variant?: "filled" | "outlined"
  error?: boolean
  inputLimit?: number
  helperText?: string
  ChipProps?: Partial<ChipProps>
  InputProps?: Partial<InputProps>
  nativeInputProps?: React.InputHTMLAttributes<HTMLInputElement>
  renderOption?: (
    props: React.HTMLAttributes<HTMLDivElement>,
    option: Option | Option[] | string | string[],
    state: AutocompleteRenderOptionState
  ) => React.ReactNode
  formatInputValue?: (option: Option) => string
  getChipLabel?: (option: Option) => React.ReactNode
  classes?: Partial<Record<StyleName, string>>
  valueAsObject?: boolean
  dataAutomationId?: string
  breadcrumbs?: React.ReactNode
  // Notice rendered in the dropdown header, above the breadcrumbs and options.
  headerNotice?: React.ReactNode
  // Helps avoid jumps to top in long dropdown lists.
  preserveListboxScrollTop?: boolean
}

const componentsProps = {
  clearIndicator: { tabIndex: 0 },
  popupIndicator: { disableRipple: true }
}

const Autocomplete = forwardRef<HTMLDivElement, AutocompleteProps>(
  (
    {
      value,
      onChange,
      inputValue,
      onInputChange,
      options,
      id,
      disabled = false,
      label = "",
      inline = false,
      className,
      placeholder = "",
      size = "medium" as const,
      multiple = false,
      withCheckbox = false,
      withAdormentBorder = true,
      inputStartAdornment,
      variant = "outlined" as const,
      error = false,
      inputLimit,
      helperText,
      renderOption,
      formatInputValue,
      getChipLabel,
      classes: outerClasses,
      disableCloseOnSelect = false,
      limitTags,
      ChipProps = {},
      InputProps: OuterInputProps = {} as InputProps,
      nativeInputProps: outerNativeInputProps = {},
      valueAsObject = false,
      dataAutomationId,
      breadcrumbs,
      headerNotice,
      open,
      preserveListboxScrollTop = false,
      ...rest
    },
    ref
  ) => {
    const { cx, classes } = useStyles({
      error,
      inline,
      variant,
      withAdormentBorder
    })

    // Key the memo on a stable signature of value + option contents so it
    // recomputes on real changes, not on every fresh array reference.
    const valueKey = JSON.stringify(value ?? null)
    const optionsKey = JSON.stringify(
      options.map(option => [option.value, option.label])
    )

    const valueOption = useMemo(() => {
      // Fix format of value based on multiple or single selection
      let copiedValue = value
      if (multiple) {
        copiedValue = Array.isArray(value) ? value : [value]
      } else {
        copiedValue =
          Array.isArray(value) && value.length > 0 ? value[0] : value
      }
      const option = multiple
        ? options.filter(option => copiedValue?.includes(option.value))
        : options.find(option => option.value === copiedValue)
      return option ?? null
    }, [valueKey, optionsKey, multiple])

    const { listboxRef, handleListboxScroll } = useListboxScrollTop({
      preserveListboxScrollTop,
      open,
      valueOption,
      optionsLength: options.length
    })

    const handleChange = useCallback(
      (
        event: React.SyntheticEvent,
        option: Option | Option[],
        reason: AutocompleteChangeReason,
        details: AutocompleteChangeDetails<Option>
      ) => {
        onChange?.(
          event,
          Array.isArray(option)
            ? option.map(({ value }) => value)
            : option?.value ?? null,
          reason,
          details
        )
      },
      [onChange]
    )

    return (
      <MUIAutocomplete
        ref={ref}
        id={id}
        size={size}
        className={cx(classes.container, outerClasses?.container, className)}
        {...(open !== undefined && open !== null ? { open } : {})}
        options={options}
        value={valueOption}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={onInputChange}
        disableCloseOnSelect={disableCloseOnSelect}
        disabled={disabled}
        multiple={multiple}
        data-automation-id={
          dataAutomationId
            ? formatDataAutomationId(dataAutomationId)
            : undefined
        }
        classes={{
          ...outerClasses,
          endAdornment: cx(classes.endAdornment, outerClasses?.endAdornment),
          popupIndicator: cx(
            classes.popupIndicator,
            outerClasses?.popupIndicator
          ),
          clearIndicator: cx(
            classes.clearIndicator,
            outerClasses?.clearIndicator
          ),
          popper: cx(classes.popper, outerClasses?.popper),
          paper: cx(classes.paper, outerClasses?.paper),
          listbox: cx(classes.listbox, outerClasses?.listbox),
          tag: cx(classes.tag, outerClasses?.tag)
        }}
        componentsProps={componentsProps}
        ListboxProps={{
          ref: listboxRef,
          onScroll: handleListboxScroll,
          // a11y: a multi-select listbox must advertise aria-multiselectable so
          // screen readers announce each option's selected state (SC 4.1.2)
          ...(multiple && { "aria-multiselectable": true }),
          ...(dataAutomationId && {
            "data-automation-id": formatDataAutomationId(
              `${dataAutomationId}-options-list`
            )
          })
        }}
        clearIcon={<FaIcon solid name="times-circle" size="xsmall" />}
        popupIcon={<FaIcon solid name="caret-down" />}
        isOptionEqualToValue={(option, item) => option.value === item.value}
        renderInput={({
          id,
          disabled,
          fullWidth,
          size,
          InputProps,
          inputProps
        }) => {
          const endAdorment = (OuterInputProps?.endAdornment ??
            InputProps.endAdornment) as JSX.Element
          const wrappedAdorment = endAdorment ? (
            <endAdorment.type key={endAdorment.key} {...endAdorment.props}>
              {React.Children.toArray(endAdorment.props.children).map(
                (item: JSX.Element) => (
                  <Box
                    key={item.key}
                    className={cx(
                      classes.adornmentWrapper,
                      outerClasses?.adornmentWrapper
                    )}
                  >
                    {item}
                  </Box>
                )
              )}
            </endAdorment.type>
          ) : null

          const currentOption = Array.isArray(valueOption)
            ? valueOption.length > 0
              ? valueOption[0]
              : null
            : valueOption
          const renderValue =
            formatInputValue?.(currentOption) ?? inputProps.value

          return (
            <FormControl
              id={id}
              disabled={disabled}
              fullWidth={fullWidth}
              size={size}
              className={cx(classes.control, outerClasses?.control)}
            >
              {label && <InputLabel>{label}</InputLabel>}
              {currentOption?.icon && (
                <Stack
                  direction="row"
                  spacing={2}
                  className={cx(classes.placeholder, outerClasses?.placeholder)}
                >
                  <Typography variant="body2" sx={{ visibility: "hidden" }}>
                    {inputProps.value}
                  </Typography>
                  {currentOption.icon}
                </Stack>
              )}
              <Input
                {...InputProps}
                {...OuterInputProps}
                disableUnderline
                endAdornment={wrappedAdorment}
                startAdornment={
                  (!!inputStartAdornment ||
                    !!InputProps.startAdornment ||
                    !!OuterInputProps?.startAdornment) && (
                    <Stack
                      gap={1}
                      overflow="auto"
                      direction="row"
                      alignItems="center"
                    >
                      {inputStartAdornment}
                      {!!(
                        OuterInputProps?.startAdornment ??
                        InputProps.startAdornment
                      ) && (
                        <Stack
                          className={cx(
                            classes.tagsWrapper,
                            outerClasses?.tagsWrapper
                          )}
                        >
                          {OuterInputProps?.startAdornment ??
                            InputProps.startAdornment}
                        </Stack>
                      )}
                    </Stack>
                  )
                }
                placeholder={placeholder}
                inputProps={{
                  ...inputProps,
                  ...(!!inputLimit ? { maxLength: inputLimit } : {}),
                  value: renderValue,
                  ...outerNativeInputProps
                }}
                data-automation-id={
                  dataAutomationId
                    ? formatDataAutomationId(`${dataAutomationId}-search-bar`)
                    : undefined
                }
              />

              {!!helperText && (
                <FormHelperText
                  error={error}
                  className={cx(classes.helperText, outerClasses?.helperText)}
                >
                  {helperText}
                </FormHelperText>
              )}
              {headerNotice && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="muted">
                    {headerNotice}
                  </Typography>
                </Box>
              )}
              {breadcrumbs}
            </FormControl>
          )
        }}
        renderOption={
          renderOption ||
          ((props, option: Option, { selected }) => (
            <Stack
              {...props}
              key={option.value}
              component="li"
              className={cx(classes.item, outerClasses?.item, {
                [classes.itemSelected]: selected
              })}
            >
              {withCheckbox && !option.inputValue && (
                <Checkbox
                  checked={selected}
                  className={cx(classes.checkbox, outerClasses?.checkbox)}
                  // a11y: the option row (role=option + aria-selected) conveys
                  // name + state; hide the decorative checkbox (SC 4.1.2)
                  aria-hidden
                  inputProps={{ tabIndex: -1 }}
                />
              )}
              <Typography variant="body2">{option.label}</Typography>
              {option.icon}
            </Stack>
          ))
        }
        renderTags={(value, getTagProps) => {
          const chipVariant = variant === "outlined" ? "light" : "outlined"

          const tags = value.map((option: Option, index) => {
            const label = getChipLabel?.(option) ?? option.label
            const tagProps = getTagProps({ index })

            return (
              <Chip
                key={option.value}
                size="xs"
                variant={chipVariant}
                label={label}
                {...tagProps}
                {...ChipProps}
                // a11y: JSX chip labels can't seed the "Remove {name}" button, so
                // give each tag a distinct name from the plain-text option label
                aria-label={typeof label === "string" ? label : option.label}
              />
            )
          })

          if (!limitTags) {
            return tags
          }

          return tags
            .slice(0, limitTags)
            .concat(
              value.length > limitTags ? (
                <Chip
                  key="more"
                  size="xs"
                  variant={chipVariant}
                  label={`+${value.length - limitTags} more`}
                  className={getTagProps({ index: limitTags }).className}
                  {...ChipProps}
                />
              ) : null
            )
        }}
        {...rest}
      />
    )
  }
)

export default Autocomplete
